import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { staffApi, formatApiError } from '@/lib/api';
import { useStaffAuth } from '@/contexts/StaffAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { RedemptionCalendar } from '@/components/shared/RedemptionCalendar';
import { AddOnDialog } from '@/components/staff/AddOnDialog';

function EditSubscriptionDialog({ sub, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(sub.planId);
  const [originalCredits, setOriginalCredits] = useState(String(sub.originalCredits));
  const [remainingCredits, setRemainingCredits] = useState(String(sub.remainingCredits));
  const [finalAmount, setFinalAmount] = useState(String(Number(sub.finalAmount ?? 0)));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      staffApi.get('/plans', { params: { active: true } }).then(({ data }) => setPlans(data.plans));
      setPlanId(sub.planId);
      setOriginalCredits(String(sub.originalCredits));
      setRemainingCredits(String(sub.remainingCredits));
      setFinalAmount(String(Number(sub.finalAmount ?? 0)));
      setReason('');
    }
  }, [open, sub]);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('A reason is required');
      return;
    }
    setBusy(true);
    try {
      await onConfirm({ planId, originalCredits, remainingCredits, finalAmount, reason });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="action-edit-open-btn" className="bg-saffron hover:bg-saffron-hover">Edit Subscription</Button>
      </DialogTrigger>
      <DialogContent data-testid="dialog-edit-subscription">
        <DialogHeader>
          <DialogTitle>Edit Subscription</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-ink">Changes apply immediately to what the customer sees. Adjust amounts manually as needed.</p>
        <div className="space-y-1.5">
          <Label>Plan</Label>
          <Select value={planId} onValueChange={setPlanId}>
            <SelectTrigger data-testid="edit-plan-select"><SelectValue placeholder="Select a plan" /></SelectTrigger>
            <SelectContent>
              {plans.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name} — {p.tiffinCount} tiffins — ${Number(p.price).toFixed(2)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Original Credits</Label>
            <Input type="number" data-testid="edit-original-credits-input" value={originalCredits} onChange={(e) => setOriginalCredits(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Remaining Credits</Label>
            <Input type="number" data-testid="edit-remaining-credits-input" value={remainingCredits} onChange={(e) => setRemainingCredits(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Amount Paid ($)</Label>
          <Input type="number" step="0.01" data-testid="edit-final-amount-input" value={finalAmount} onChange={(e) => setFinalAmount(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Reason (required)</Label>
          <Textarea data-testid="edit-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <DialogFooter>
          <Button data-testid="edit-confirm-btn" disabled={busy} onClick={submit} className="bg-saffron hover:bg-saffron-hover">
            {busy ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActionDialog({ trigger, title, description, onConfirm, requireReason, requireDays, requireAmount, presets }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm({ reason, days, amount });
      setOpen(false);
      setReason('');
      setDays('');
      setAmount('');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid={`dialog-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {description && <p className="text-sm text-muted-ink">{description}</p>}
        {requireAmount && (
          <div className="space-y-1.5">
            <Label>Amount (use negative to remove credits)</Label>
            <Input type="number" data-testid="credit-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
        )}
        {requireDays && (
          <div className="space-y-1.5">
            <Label>Number of days</Label>
            <Input type="number" data-testid="extend-days-input" value={days} onChange={(e) => setDays(e.target.value)} />
          </div>
        )}
        {requireReason && (
          <div className="space-y-1.5">
            <Label>Reason (required)</Label>
            {presets?.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="reason-presets">
                {presets.map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={`reason-preset-${p.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                    onClick={() => setReason(p)}
                    className={`text-xs rounded-full px-2.5 py-1 border transition-colors duration-150 ${
                      reason === p ? 'bg-saffron text-white border-saffron' : 'bg-white text-charcoal border-border/40 hover:border-saffron/50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            <Textarea data-testid="action-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        )}
        <DialogFooter>
          <Button data-testid="action-confirm-btn" disabled={busy} onClick={submit} className="bg-saffron hover:bg-saffron-hover">
            {busy ? 'Working…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CANCEL_REASON_PRESETS = [
  'Moving away',
  'Too expensive',
  'Not satisfied with food',
  'Health/dietary reasons',
  'Switching plan',
  'Created by mistake',
  'Customer request',
];

export default function SubscriptionDetail() {
  const { id } = useParams();
  const { hasRole } = useStaffAuth();
  const [sub, setSub] = useState(null);
  const [addOnOpen, setAddOnOpen] = useState(false);
  const canManage = hasRole('ADMIN', 'MANAGER');

  const load = () => staffApi.get(`/subscriptions/${id}`).then(({ data }) => setSub(data.subscription));

  useEffect(() => {
    load();
  }, [id]);

  if (!sub) return <p className="text-muted-ink text-sm">Loading subscription…</p>;

  const act = async (method, path, body = {}) => {
    if (method === 'post') await staffApi.post(`/subscriptions/${id}${path}`, body);
    else await staffApi.patch(`/subscriptions/${id}${path}`, body);
    toast.success('Updated');
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-700 text-charcoal" data-testid="subscription-detail-customer">
            {sub.customer.name}
          </h1>
          <p className="text-xs text-muted-ink mt-0.5" data-testid="subscription-detail-code">
            {sub.subscriptionCode} · {sub.customer.phone}
          </p>
        </div>
        <span className="text-xs font-semibold px-3 py-1 rounded-full bg-bone text-charcoal" data-testid="subscription-detail-status">
          {sub.status}
        </span>
      </div>

      {sub.status === 'CANCELLED' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="cancellation-info">
          <div>
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Cancelled</p>
            <p className="text-sm text-charcoal">
              {sub.cancelledAt ? new Date(sub.cancelledAt).toLocaleString('en-CA') : '—'}
            </p>
            <p className="text-sm text-muted-ink mt-1">
              Reason: {sub.cancellationReason || '—'}
            </p>
          </div>
          {canManage && (
            <Button
              data-testid="action-reactivate-btn"
              variant="outline"
              className="border-coriander text-coriander hover:bg-coriander/10"
              onClick={() => act('patch', '/reactivate')}
            >
              Reactivate
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ['Plan', sub.plan.name],
          ['Original', sub.originalCredits],
          ['Used', sub.usedCredits],
          ['Remaining', sub.remainingCredits],
          ['Purchase Date', new Date(sub.purchaseDate).toLocaleDateString('en-CA')],
          ['Start Date', new Date(sub.startDate).toLocaleDateString('en-CA')],
          ['Expiry Date', new Date(sub.expiryDate).toLocaleDateString('en-CA')],
          ['Payment Status', sub.paymentStatus],
        ].map(([label, value]) => (
          <div
            key={label}
            data-testid={`sub-stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            className="bg-white rounded-xl border border-border/40 p-4 text-sm"
          >
            <p className="text-muted-ink text-xs mb-1">{label}</p>
            <p className="text-charcoal font-medium">{value}</p>
          </div>
        ))}
      </div>

      {sub.status === 'ACTIVE' && (
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="action-addon-open-btn"
            variant="outline"
            onClick={() => setAddOnOpen(true)}
          >
            Add Add-on (in-store)
          </Button>
        </div>
      )}

      <AddOnDialog
        open={addOnOpen}
        onOpenChange={setAddOnOpen}
        subscriptionId={sub.id}
        remainingCredits={sub.remainingCredits}
        customerName={sub.customer.name}
        onDone={() => { setAddOnOpen(false); load(); }}
      />

      {sub.subscriptionItems?.length > 0 && (
        <div className="bg-white rounded-xl border border-border/40" data-testid="subscription-addons">
          <h2 className="font-heading font-semibold text-charcoal p-4 pb-0">Add-ons</h2>
          <div className="divide-y divide-border/30">
            {sub.subscriptionItems.map((it) => (
              <div key={it.id} className="p-3 flex justify-between gap-3 text-sm px-4" data-testid="subscription-addon-row">
                <span className="text-charcoal">{it.itemName}</span>
                <span className="text-muted-ink">
                  ${Number(it.itemPrice).toFixed(2)} · {new Date(it.createdAt).toLocaleDateString('en-CA')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {canManage && (
        <div className="flex flex-wrap gap-2">
          <EditSubscriptionDialog
            sub={sub}
            onConfirm={({ planId, originalCredits, remainingCredits, finalAmount, reason }) =>
              act('patch', '/edit', { planId, originalCredits, remainingCredits, finalAmount, reason })
            }
          />
          {sub.status === 'ACTIVE' && (
            <Button data-testid="action-pause-btn" variant="outline" onClick={() => act('patch', '/pause')}>Pause</Button>
          )}
          {sub.status === 'PAUSED' && (
            <Button data-testid="action-resume-btn" variant="outline" onClick={() => act('patch', '/resume')}>Resume</Button>
          )}
          <ActionDialog
            trigger={<Button data-testid="action-extend-open-btn" variant="outline">Extend</Button>}
            title="Extend Subscription"
            requireDays
            requireReason
            onConfirm={({ days, reason }) => act('patch', '/extend', { days: parseInt(days), reason })}
          />
          <ActionDialog
            trigger={<Button data-testid="action-credits-open-btn" variant="outline">Add / Remove Credits</Button>}
            title="Adjust Credits"
            requireAmount
            requireReason
            onConfirm={({ amount, reason }) => act('post', '/credits', { amount: parseInt(amount), reason })}
          />
          <ActionDialog
            trigger={<Button data-testid="action-cancel-open-btn" variant="outline" className="text-destructive">Cancel</Button>}
            title="Cancel Subscription"
            requireReason
            presets={CANCEL_REASON_PRESETS}
            onConfirm={({ reason }) => act('patch', '/cancel', { reason })}
          />
        </div>
      )}

      <div className="bg-white rounded-xl border border-border/40">
        <h2 className="font-heading font-semibold text-charcoal p-4 pb-0">Credit / Tiffin Ledger</h2>
        <div className="divide-y divide-border/30">
          {sub.transactions.map((t) => (
            <div key={t.id} className="p-3 flex flex-wrap gap-x-3 gap-y-1 text-sm px-4" data-testid="ledger-row">
              <span className="text-muted-ink">{new Date(t.createdAt).toLocaleString('en-CA')}</span>
              <span>{t.transactionType}</span>
              <span className={t.changeAmount > 0 ? 'text-coriander' : 'text-saffron'}>
                {t.changeAmount > 0 ? '+' : ''}{t.changeAmount}
              </span>
              <span className="text-muted-ink">{t.previousBalance} → {t.newBalance}</span>
              <span className="text-muted-ink">{t.staff?.name || '—'}</span>
              {t.notes && <span className="text-muted-ink italic w-full" data-testid="ledger-note">{t.notes}</span>}
            </div>
          ))}
          {sub.transactions.length === 0 && <p className="p-4 text-sm text-muted-ink">No transactions yet.</p>}
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="font-heading font-semibold text-charcoal">Tiffin Redemption Calendar</h2>
        <RedemptionCalendar redemptions={sub.redemptions} showStaff />
      </div>

      <div className="bg-white rounded-xl border border-border/40">
        <h2 className="font-heading font-semibold text-charcoal p-4 pb-0">Redemption History</h2>
        <div className="divide-y divide-border/30">
          {sub.redemptions.map((r) => (
            <div key={r.id} className="p-3 flex flex-wrap gap-x-3 gap-y-1 text-sm px-4" data-testid="subscription-redemption-row">
              <span className="text-muted-ink">{new Date(r.createdAt).toLocaleString('en-CA')}</span>
              <span>-{r.quantity}</span>
              <span className="text-muted-ink">{r.balanceBefore} → {r.balanceAfter}</span>
              <span className="text-muted-ink">{r.staff?.name} ({r.method})</span>
            </div>
          ))}
          {sub.redemptions.length === 0 && <p className="p-4 text-sm text-muted-ink">No redemptions yet.</p>}
        </div>
      </div>
    </div>
  );
}
